import { fetchWithAuth } from '../utils/apiTransport';

export interface FOSSearchParams {
  query: string;
  limit?: number;
  offset?: number;
  outcome?: string;
  product_category?: string;
  company?: string;
  min_compensation?: number;
  max_compensation?: number;
  date_from?: string;
  date_to?: string;
  year?: number;
  use_semantic?: boolean;
}

export interface FOSDecisionCard {
  id: string;
  reference: string;
  decision_date: string | null;
  outcome: string;
  company: string | null;
  product_category: string | null;
  complaint_type: string | null;
  core_issue: string | null;
  total_compensation: number | null;
  similarity_score: number | null;
}

export interface FOSSearchResult {
  decisions: FOSDecisionCard[];
  total: number;
  limit: number;
  offset: number;
  query: string;
  search_mode: string;
}

export interface FOSFacets {
  outcomes: Record<string, number>;
  products: Record<string, number>;
  companies: Record<string, number>;
  compensation_ranges: Record<string, number>;
  years: Record<number, number>;
}

export interface FOSCorpusStats {
  total_decisions: number;
  searchable_decisions: number;
  average_compensation: number | null;
}

export interface FOSDecisionDetail {
  id: string;
  reference: string;
  decision_date: string | null;
  outcome: string;
  company: string | null;
  product_category: string | null;
  complaint_type: string | null;
  core_issue: string | null;
  background: string | null;
  key_facts: string[] | null;
  customer_position: string | null;
  company_position: string | null;
  reasoning_summary: string | null;
  consumer_duty_relevant: boolean;
  redress: {
    total_compensation?: number | null;
    distress_inconvenience?: number | null;
    financial_loss?: number | null;
    interest?: number | null;
    other_monetary?: number | null;
    non_monetary?: string[] | null;
    notes?: string | null;
  } | null;
  pdf_url: string | null;
  full_text: string | null;
  processing_status: string;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Search FOS decisions using semantic search (POST)
 */
export const searchFOSDecisions = (params: FOSSearchParams): Promise<FOSSearchResult> => {
  return fetchWithAuth('/v1/fos/search', {
    method: 'POST',
    body: JSON.stringify(params)
  });
};

/**
 * Search FOS decisions using semantic search (GET) - simpler queries
 */
export const searchFOSDecisionsGet = (
  query: string,
  options: Omit<FOSSearchParams, 'query'> = {}
): Promise<FOSSearchResult> => {
  const params = new URLSearchParams({ q: query });

  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());
  if (options.outcome) params.append('outcome', options.outcome);
  if (options.product_category) params.append('product_category', options.product_category);
  if (options.company) params.append('company', options.company);
  if (options.min_compensation) params.append('min_compensation', options.min_compensation.toString());
  if (options.max_compensation) params.append('max_compensation', options.max_compensation.toString());
  if (options.date_from) params.append('date_from', options.date_from);
  if (options.date_to) params.append('date_to', options.date_to);
  if (options.year) params.append('year', options.year.toString());
  if (options.use_semantic !== undefined) params.append('semantic', options.use_semantic.toString());

  return fetchWithAuth(`/v1/fos/search?${params.toString()}`);
};

/**
 * Get FOS facets for filtering UI
 */
export const getFOSFacets = (): Promise<FOSFacets> => {
  return fetchWithAuth('/v1/fos/facets');
};

/**
 * Get FOS corpus statistics
 */
export const getFOSStats = (): Promise<FOSCorpusStats> => {
  return fetchWithAuth('/v1/fos/stats');
};

/**
 * Get FOS decision details by ID
 */
export const getFOSDecisionById = (decisionId: string): Promise<FOSDecisionDetail> => {
  return fetchWithAuth(`/v1/fos/decisions/${decisionId}`);
};

/**
 * Get similar FOS decisions for a signal
 */
export const getSimilarFOSDecisions = (
  signalId: string,
  limit: number = 5,
  threshold: number = 0.5
): Promise<FOSDecisionCard[]> => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    threshold: threshold.toString()
  });
  return fetchWithAuth(`/v1/fos/similar/${signalId}?${params.toString()}`);
};

// FOS Projection Types (Phase 2)
export interface FOSProjectionPoint {
  decision_id: string;
  x: number;
  y: number;
  cluster_id: number | null;
  outcome: string | null;
  product_category: string | null;
  total_compensation: number | null;
}

export interface FOSClusterInfo {
  cluster_num: number;
  label: string;
  description: string | null;
  centroid_x: number;
  centroid_y: number;
  decision_count: number;
  outcome_distribution: Record<string, number> | null;
  avg_compensation: number | null;
  top_products: string[] | null;
}

export interface FOSProjectionData {
  points: FOSProjectionPoint[];
  clusters: FOSClusterInfo[];
  bounds: {
    x_min: number | null;
    x_max: number | null;
    y_min: number | null;
    y_max: number | null;
  };
  total_points: number;
  total_clusters: number;
  noise_count: number;
  computed_at: string | null;
}

/**
 * Get FOS projection data for visualization
 */
export const getFOSProjections = (
  includeClusters: boolean = true
): Promise<FOSProjectionData> => {
  const params = new URLSearchParams({
    include_clusters: includeClusters.toString()
  });
  return fetchWithAuth(`/v1/fos/projections?${params.toString()}`);
};

// Signal projection types
export interface FOSSignalProjectionPoint {
  signal_id: string;
  x: number;
  y: number;
  nearest_decision_ids: string[] | null;
  nearest_similarity_scores: number[] | null;
  severity_score: number | null;
  complaint_detected: boolean | null;
}

export interface FOSSignalProjectionsResponse {
  signals: FOSSignalProjectionPoint[];
  total: number;
  days: number;
}

export interface FOSSignalProjectionRefreshResponse {
  status: string;
  task_id?: string;
  message?: string;
  days: number;
}

/**
 * Get signal projections overlaid on FOS space
 */
export const getFOSSignalProjections = (
  days: number = 30
): Promise<FOSSignalProjectionsResponse> => {
  const params = new URLSearchParams({
    days: days.toString()
  });
  return fetchWithAuth(`/v1/fos/projections/signals?${params.toString()}`);
};

/**
 * Trigger a refresh of signal projections for overlay visualization
 */
export const refreshFOSSignalProjections = (
  days: number = 30
): Promise<FOSSignalProjectionRefreshResponse> => {
  const params = new URLSearchParams({
    days: days.toString()
  });
  return fetchWithAuth(`/v1/fos/projections/signals/refresh?${params.toString()}`, {
    method: 'POST'
  });
};
